import React from 'react';

import {useStore} from 'effector-react';
import {useTranslation} from 'react-i18next';
import {generatePath, useHistory} from 'react-router-dom';

import {ReactComponent as FilterIcon} from 'assets/svg/filter2.svg';
import {ModalFirsError} from 'components/common/ModalFirsError';
import {Page} from 'components/common/Page';
import {PermissionAccess} from 'components/common/PermissionAccess';
import {CheckboxArray} from 'components/ui-kit/CheckboxArray';
import {DatePicker} from 'components/ui-kit/DatePicker';
import {DateRange} from 'components/ui-kit/DateRange';
import {Icon} from 'components/ui-kit/Icon';
import {Link} from 'components/ui-kit/Link';
import {StatusWrapper} from 'components/ui-kit/PageComponents';
import {Select} from 'components/ui-kit/Select';
import {DOCUMENT_STATUS_COLORS} from 'core/constants/statuses';
import {DocumentStatus, DocumentType, DocumentTypeEntity} from 'core/dictionaries';
import {ROUTE} from 'core/routes';
import {isCustomsRole$} from 'models/auth';
import {listState$, showConfigBlockChanged} from 'models/documents/documents/grid/list';
import {$invoicesFilters} from 'models/documents/invoices/common';
import {breakpoint, colors} from 'theme';
import {
    DocumentsAccessors,
    DocumentsGridFormattedRow,
    DocumentsGridRawRow,
    UserPermission,
} from 'types';
import {useMatchMedia} from 'utils/custom-hooks';
import {formatDate} from 'utils/date';
import {handleNil} from 'utils/handleNil';
import {mapArrayToI18nOptions, mapEnumToI18nOptions} from 'utils/select';

import {GridsNavigation} from '../GridsNavigation';

import {Controls} from './Controls';
import {DocumentsGridList} from './List';
import {ControlsWrapper, HeaderControls, NavigationWrapper} from './styled';
import {DocumentsGridTable} from './Table';
import {TableSearch} from './Table/search';

// reportId createdAt seller/buyer status

const getConfig = (t: Function, showType: boolean) => {
    const INVOICES = [
        ROUTE.DOCUMENTS_INVOICES,
        ROUTE.DOCUMENTS_INVOICES_INCOMING,
        ROUTE.DOCUMENTS_INVOICES_OUTGOING,
    ];

    return [
        ...(INVOICES.some((i) => i === location.pathname)
            ? [
                  {
                      id: DocumentsAccessors.irn,
                      header: t('pages.documents.documents.irn'),
                      isSortable: false,
                      width: 260,
                  },
              ]
            : []),
        {
            id: DocumentsAccessors.id,
            header: t('pages.documents.documents.id'),
            // filter: <Input placeholder={t('Enter value')} />,
            isSortable: false,
            width: 300,
        },
        {
            id: DocumentsAccessors.createdDate,
            header: t('pages.documents.documents.createdDate'),
            filter: INVOICES.some((i) => i === location.pathname) ? <DateRange /> : <DatePicker />,
            isSortable: true,
        },
        ...(!INVOICES.some((i) => i === location.pathname)
            ? [
                  {
                      id: DocumentsAccessors.type,
                      header: t('pages.documents.documents.docType'),
                      filter: showType ? (
                          <Select
                              options={mapArrayToI18nOptions(
                                  Object.keys(DocumentTypeEntity)
                                      .filter((key) => key !== 'UPD')
                                      .map((key) => ({key, value: key})),
                                  'dictionaries.documents.type',
                              )}
                          />
                      ) : null,
                      isSortable: false,
                  },
              ]
            : []),
        ...(location.pathname === ROUTE.DOCUMENTS_INVOICES_INCOMING
            ? [
                  {
                      id: DocumentsAccessors.seller,
                      header: t('pages.documents.documents.seller'),
                      filter: (
                          <CheckboxArray
                              searchable={true}
                              placeholder="Enter company name"
                              name="seller-names"
                              options={() =>
                                  $invoicesFilters.getState().seller
                                      ? $invoicesFilters.getState().seller.map((i, index) => ({
                                            id: `${index}`,
                                            value: i,
                                            label: i.toLowerCase(),
                                        }))
                                      : []
                              }
                          />
                      ),
                      isSortable: true,
                  },
              ]
            : []),
        ...(location.pathname === ROUTE.DOCUMENTS_INVOICES_OUTGOING
            ? [
                  {
                      id: DocumentsAccessors.buyer,
                      header: t('pages.documents.documents.buyer'),
                      filter: (
                          <CheckboxArray
                              searchable={true}
                              placeholder="Enter company name"
                              name="buyers-name"
                              options={() =>
                                  $invoicesFilters.getState().buyer
                                      ? $invoicesFilters.getState().buyer.map((i, index) => ({
                                            id: `${index}`,
                                            value: i,
                                            label: i.toLowerCase(),
                                        }))
                                      : []
                              }
                          />
                      ),
                      isSortable: true,
                  },
              ]
            : []),
        ...(INVOICES.some((i) => i === location.pathname)
            ? [
                  {
                      id: DocumentsAccessors.buyerLocation,
                      header: t('pages.documents.documents.customerstorageLocation'),
                      filter: (
                          <CheckboxArray
                              searchable={true}
                              placeholder="Enter the name or address"
                              name="store-locations"
                              options={() =>
                                  $invoicesFilters.getState().buyerLocation
                                      ? $invoicesFilters
                                            .getState()
                                            .buyerLocation.map((i, index) => {
                                                const {storage, address} = (i as unknown) as {
                                                    storage: string;
                                                    address: string;
                                                };
                                                return {
                                                    id: `${index}`,
                                                    value: storage.toString(),
                                                    label: storage.toLowerCase(),
                                                    text: address,
                                                };
                                            })
                                      : []
                              }
                          />
                      ),
                      isSortable: false,
                  },
              ]
            : []),
        ...(INVOICES.some((i) => i === location.pathname)
            ? []
            : [
                  {
                      id: DocumentsAccessors.owner,
                      header: t('pages.documents.documents.owner'),
                      // filter: <Input placeholder={t('Enter value')} />,
                      isSortable: false,
                  },
              ]),
        {
            id: DocumentsAccessors.status,
            header: t('pages.documents.documents.status'),
            filter: INVOICES.some((i) => i === location.pathname) ? (
                <CheckboxArray
                    name="doc-status-names"
                    options={() =>
                        $invoicesFilters.getState().status
                            ? $invoicesFilters.getState().status.map((i, index) => ({
                                  id: `${index}`,
                                  value: i,
                                  label: t(`dictionaries.documents.status.${i}`),
                              }))
                            : []
                    }
                />
            ) : (
                <Select
                    options={mapEnumToI18nOptions(DocumentStatus, 'dictionaries.documents.status')}
                    isClearable
                />
            ),
            isSortable: false,
        },
    ];
};

export function convertData(
    data: DocumentsGridRawRow[] | undefined,
    t: Function,
    lastCreatedDocumentId?: string | null,
    updOnly?: boolean,
): DocumentsGridFormattedRow[] {
    if (!data) {
        return [];
    }

    const getColors = (status: DocumentStatus) => ({
        color: DOCUMENT_STATUS_COLORS[status ?? 'DEFAULT'],
    });

    return data.map((elem) => ({
        [DocumentsAccessors.irn]: handleNil(elem.irn),
        [DocumentsAccessors.id]: handleNil(elem, (value: DocumentsGridRawRow) => (
            <PermissionAccess
                permissions={[UserPermission.DOCUMENT_DETAIL_VIEW]}
                placeholder={value.reportId}
            >
                <Link
                    to={{
                        pathname: updOnly
                            ? generatePath(ROUTE.INVOICES_DETAIL, {id: value.reportId})
                            : generatePath(ROUTE.DOCUMENTS_DETAIL, {id: value.reportId}),
                        state: {from: location.pathname},
                    }}
                >
                    <span
                        style={{
                            fontWeight: value.reportId === lastCreatedDocumentId ? 'bold' : 'unset',
                        }}
                    >
                        {value.reportId}
                    </span>
                </Link>
            </PermissionAccess>
        )),
        [DocumentsAccessors.createdDate]: handleNil(elem.createdAt, (value: string) =>
            formatDate(new Date(value), 'dd.MM.yyyy HH:mm'),
        ),
        [DocumentsAccessors.seller]: handleNil(elem.seller),
        [DocumentsAccessors.buyer]: handleNil(elem.buyer),
        [DocumentsAccessors.owner]: handleNil(elem.owner),
        [DocumentsAccessors.buyerLocation]: handleNil(elem.buyerLocation),
        [DocumentsAccessors.type]: handleNil(elem.type, (value: DocumentType) =>
            t('dictionaries.documents.type.' + value),
        ),
        [DocumentsAccessors.status]: handleNil(elem.status, (value: DocumentStatus) => (
            <StatusWrapper {...getColors(value)}>
                {t('dictionaries.documents.status.' + value)}
            </StatusWrapper>
        )),
    }));
}
export const PageToRedirect = ({redirectTo}: Record<string, string>) => {
    const history = useHistory();
    history.push(redirectTo);

    return <></>;
};

export const DocumentsGridPage = () => {
    const {t} = useTranslation();
    const isMobile = useMatchMedia(`(max-width: ${breakpoint.phone})`);
    const {showConfigBlock} = useStore(listState$);
    const isCustomsRole = useStore(isCustomsRole$);

    const headerControls = (
        <HeaderControls>
            <ControlsWrapper>
                <TableSearch />
                {!isCustomsRole && <Controls />}
            </ControlsWrapper>
            <NavigationWrapper needMargin>
                {!isCustomsRole && <GridsNavigation />}
            </NavigationWrapper>
        </HeaderControls>
    );

    const pageHeaderControls = isMobile ? (
        <Icon
            source={FilterIcon}
            width={18}
            height={18}
            onClick={() => showConfigBlockChanged(!showConfigBlock)}
            fillColor={showConfigBlock ? colors.brandPrimary : colors.black}
        />
    ) : (
        undefined
    );

    return (
        <Page title={t('menu.Documents')} controls={pageHeaderControls}>
            <ModalFirsError />
            {!isMobile && (
                <DocumentsGridTable
                    configFunc={getConfig}
                    dataMapper={convertData}
                    headerControls={headerControls}
                    // exportConfig={exportConfig}
                />
            )}
            {isMobile && (
                <DocumentsGridList
                    configFunc={getConfig}
                    dataMapper={convertData}
                    headerControls={headerControls}
                    // exportConfig={exportConfig}
                />
            )}
        </Page>
    );
};
